import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Calendar, TrendingUp, Sparkles, Download } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import { api, formatApiErrorDetail } from "@/lib/api";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function Report() {
  const { profileId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get(`/profiles/${profileId}/report`);
        setData(r);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [profileId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="report-error">
        <div className="display text-2xl text-[color:var(--coral-deep)]">{error}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="report-loading">
        <div className="display text-2xl">Building report…</div>
      </div>
    );
  }

  const { profile, summary, top_tiles, category_coverage, series_30d, generated_at } = data;
  const peakWord = top_tiles[0];
  const taps = series_30d.reduce((acc, r) => acc + (r.taps || 0), 0);
  const peakDay = series_30d.reduce((best, r) => (r.taps > (best?.taps || 0) ? r : best), null);

  const exportCsv = () => {
    const rows = [
      ["section", "key", "value"],
      ["summary", "name", profile.name],
      ["summary", "language", profile.language],
      ["summary", "generated_at", generated_at],
      ["summary", "total_tiles", summary.total_tiles],
      ["summary", "tiles_used", summary.tiles_used],
      ["summary", "total_taps", summary.total_taps],
      ["summary", "days_active_30d", summary.days_active_30d],
      ["summary", "last_tapped", summary.last_tapped || ""],
      ...top_tiles.map((t, i) => ["top_tile", `${i + 1}-${t.label}`, t.tap_count]),
      ...category_coverage.map((c) => ["coverage", c.category, `${c.used}/${c.tiles} (${c.pct}%)`]),
      ...series_30d.map((s) => ["daily", s.day, `taps=${s.taps},unique=${s.unique_words}`]),
    ];
    const csv = rows
      .map((r) => r.map((v) => {
        const s = String(v ?? "");
        return /[",\n]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
      }).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sonspeak-report-${profile.name.toLowerCase().replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen px-4 sm:px-8 py-6 print:bg-white print:p-0" data-testid="report-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .brutal-card { box-shadow: none !important; border-width: 2px !important; page-break-inside: avoid; }
          .recharts-wrapper { page-break-inside: avoid; }
        }
      `}</style>

      <div className="max-w-4xl mx-auto">
        {/* Toolbar — hidden in print */}
        <div className="flex items-center gap-3 mb-6 no-print">
          <Link to={`/settings/${profileId}`} className="pill-btn" data-testid="report-back">
            <ArrowLeft size={16} />
          </Link>
          <h1 className="display text-2xl sm:text-3xl font-extrabold">Progress report</h1>
          <div className="flex-1" />
          <button
            type="button"
            className="pill-btn"
            onClick={exportCsv}
            data-testid="report-export-csv"
          >
            <Download size={16} /> CSV
          </button>
          <button
            type="button"
            className="pill-btn primary"
            onClick={() => window.print()}
            data-testid="report-print"
          >
            <Printer size={16} /> Print / save PDF
          </button>
        </div>

        {/* Letterhead */}
        <header className="brutal-card bg-[color:var(--gold)] p-6 mb-5 flex items-center gap-4">
          <div
            className="w-16 h-16 rounded-2xl border-[3px] border-[color:var(--ink)] flex items-center justify-center text-4xl"
            style={{ background: profile.color }}
            aria-hidden
          >
            {profile.avatar}
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[color:var(--ink-soft)]">SonSpeak progress report</div>
            <div className="display text-3xl font-extrabold leading-tight">{profile.name}</div>
            <div className="text-sm text-[color:var(--ink-soft)]">
              Generated {fmtDate(generated_at)} · Board started {fmtDate(profile.created_at)}
            </div>
          </div>
        </header>

        {/* Summary stats */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="report-stats">
          {[
            { label: "Total taps", value: summary.total_taps },
            { label: "Tiles used", value: `${summary.tiles_used} / ${summary.total_tiles}` },
            { label: "Days active (30d)", value: summary.days_active_30d },
            { label: "Last spoken", value: fmtDate(summary.last_tapped) },
          ].map((s) => (
            <div key={s.label} className="brutal-card bg-white p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-[color:var(--ink-soft)]">{s.label}</div>
              <div className="display text-3xl font-extrabold mt-1">{s.value}</div>
            </div>
          ))}
        </section>

        {/* 30-day chart */}
        <section className="brutal-card bg-white p-5 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={18} className="text-[color:var(--coral)]" />
            <h2 className="display text-xl font-bold">30-day vocab activity</h2>
          </div>
          <p className="text-sm text-[color:var(--ink-soft)] mb-3">
            Daily tap count and unique words spoken. {peakDay && peakDay.taps > 0 && (
              <>Peak day: <strong>{fmtDate(peakDay.day)}</strong> with <strong>{peakDay.taps}</strong> taps.</>
            )}
          </p>
          <div className="h-56" data-testid="report-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series_30d}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0000001a" />
                <XAxis
                  dataKey="day"
                  tickFormatter={(d) => d.slice(5)}
                  tick={{ fontSize: 11 }}
                  interval={Math.max(0, Math.floor(series_30d.length / 8))}
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  labelFormatter={fmtDate}
                  contentStyle={{ borderRadius: 12, border: "3px solid #1c1c1c", boxShadow: "3px 3px 0 0 #1c1c1c" }}
                />
                <Legend wrapperStyle={{ fontFamily: "Lexend, sans-serif" }} />
                <Line type="monotone" dataKey="taps" stroke="#FF6B47" strokeWidth={3} dot={{ r: 3 }} name="Taps" />
                <Line type="monotone" dataKey="unique_words" stroke="#5AA9E6" strokeWidth={3} dot={{ r: 3 }} name="Unique words" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>

        {/* Top tiles */}
        <section className="brutal-card bg-white p-5 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-[color:var(--coral)]" />
            <h2 className="display text-xl font-bold">Top 10 spoken tiles</h2>
          </div>
          {top_tiles.length === 0 ? (
            <div className="text-[color:var(--ink-soft)]">No taps yet — once {profile.name} starts speaking, the favorites will show up here.</div>
          ) : (
            <ol className="space-y-2" data-testid="report-top-tiles">
              {top_tiles.map((t, i) => {
                const max = peakWord?.tap_count || 1;
                const pct = Math.round(100 * (t.tap_count / max));
                return (
                  <li
                    key={`${t.label}-${i}`}
                    className="flex items-center gap-3 px-3 py-2 rounded-xl border-[2px] border-[color:var(--ink)] bg-[color:var(--paper-muted)]"
                  >
                    <span className="display text-sm font-bold w-6 text-right">{i + 1}.</span>
                    <span className="text-2xl" aria-hidden>{t.emoji || "💬"}</span>
                    <div className="flex-1 min-w-0">
                      <div className="display font-semibold truncate">{t.label}</div>
                      <div className="text-xs text-[color:var(--ink-soft)]">{t.category || "—"}</div>
                    </div>
                    <div className="w-32 max-w-[35%]">
                      <div className="h-2 rounded-full bg-white border-[2px] border-[color:var(--ink)] overflow-hidden">
                        <div className="h-full bg-[color:var(--coral)]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <span className="display font-bold w-10 text-right">{t.tap_count}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        {/* Category coverage */}
        <section className="brutal-card bg-white p-5 mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Calendar size={18} className="text-[color:var(--coral)]" />
            <h2 className="display text-xl font-bold">Category coverage</h2>
          </div>
          <p className="text-sm text-[color:var(--ink-soft)] mb-3">
            % of tiles in each category that have been spoken at least once.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="report-coverage">
            {category_coverage.map((c) => (
              <div
                key={c.category}
                className="flex items-center gap-3 px-3 py-2 rounded-xl border-[2px] border-[color:var(--ink)]"
                style={{ background: `${c.color}20` }}
              >
                <span className="text-2xl" aria-hidden>{c.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="display font-semibold truncate">{c.category}</div>
                  <div className="h-2 mt-1 rounded-full bg-white border-[2px] border-[color:var(--ink)] overflow-hidden">
                    <div className="h-full" style={{ width: `${c.pct}%`, background: c.color }} />
                  </div>
                </div>
                <span className="display font-bold w-12 text-right">{c.pct}%</span>
              </div>
            ))}
          </div>
        </section>

        <footer className="text-center text-xs text-[color:var(--ink-soft)] mt-4 mb-2">
          Built with SonSpeak · {taps} taps tracked across the last 30 days
        </footer>
      </div>
    </div>
  );
}
